/**
 * DnaHelix — animated double-helix backdrop for the login screen.
 *
 * Procedurally builds two interlocked Three.js helix strands of glowing
 * spheres connected by short ember-colored cylinder "rungs", slowly
 * rotating with a subtle vertical breath against the page's nexus-blue
 * gradient. No external model files — everything is built imperatively
 * using THREE primitives and attached via the R3F `onCreated` callback
 * so there are zero R3F JSX intrinsics (this avoids a babel auto-source
 * injection collision with R3F's `applyProps`).
 *
 * Engineering aesthetic chosen on purpose: Cognara is an AEC engineering
 * platform, so the visual metaphor is "structural backbone + connection"
 * — steel-blue strands + warm ember rungs matching the APS viewer brand.
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const RADIUS = 1.4;
const HEIGHT = 14;
const TURNS = 4;
const SPHERES_PER_STRAND = 80;
const RUNGS = 26;

function buildStrand(phaseOffset, baseColor, emissiveColor) {
  const group = new THREE.Group();
  const geom = new THREE.SphereGeometry(0.16, 18, 18);
  const mat = new THREE.MeshStandardMaterial({
    color: baseColor,
    emissive: emissiveColor,
    emissiveIntensity: 0.7,
    metalness: 0.4,
    roughness: 0.25,
  });
  for (let i = 0; i < SPHERES_PER_STRAND; i += 1) {
    const t = i / (SPHERES_PER_STRAND - 1);
    const angle = t * TURNS * Math.PI * 2 + phaseOffset;
    const x = Math.cos(angle) * RADIUS;
    const z = Math.sin(angle) * RADIUS;
    const y = (t - 0.5) * HEIGHT;
    const m = new THREE.Mesh(geom, mat);
    m.position.set(x, y, z);
    group.add(m);
  }
  return group;
}

function buildRungs(phaseOffsetA, phaseOffsetB) {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: '#F26B1F',
    emissive: '#9C2B0E',
    emissiveIntensity: 0.6,
    metalness: 0.55,
    roughness: 0.35,
  });
  const up = new THREE.Vector3(0, 1, 0);
  for (let i = 0; i < RUNGS; i += 1) {
    const t = (i + 0.5) / RUNGS;
    const angleA = t * TURNS * Math.PI * 2 + phaseOffsetA;
    const angleB = t * TURNS * Math.PI * 2 + phaseOffsetB;
    const y = (t - 0.5) * HEIGHT;
    const a = new THREE.Vector3(Math.cos(angleA) * RADIUS, y, Math.sin(angleA) * RADIUS);
    const b = new THREE.Vector3(Math.cos(angleB) * RADIUS, y, Math.sin(angleB) * RADIUS);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const dir = b.clone().sub(a);
    const length = dir.length();
    const geom = new THREE.CylinderGeometry(0.038, 0.038, length, 8);
    const m = new THREE.Mesh(geom, mat);
    m.position.copy(mid);
    m.quaternion.setFromUnitVectors(up, dir.clone().normalize());
    group.add(m);
  }
  return group;
}

/**
 * Pure-DOM Three.js renderer — no React Three Fiber JSX at all.
 * We attach a WebGLRenderer's canvas into our div, build the scene
 * imperatively, and drive the animation loop with requestAnimationFrame.
 * Avoids ALL R3F JSX-intrinsic issues with CRA's babel transform.
 */
export default function DnaHelix({ className = '' }) {
  const containerRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100);
    camera.position.set(0, 0, 8.5);

    // Lights
    scene.add(new THREE.AmbientLight(new THREE.Color('#90B8FF'), 0.18));
    const p1 = new THREE.PointLight(new THREE.Color('#7AB7FF'), 1.4, 30);
    p1.position.set(6, 4, 6);
    scene.add(p1);
    const p2 = new THREE.PointLight(new THREE.Color('#F26B1F'), 0.9, 20);
    p2.position.set(-5, -3, -4);
    scene.add(p2);
    const p3 = new THREE.PointLight(new THREE.Color('#ffffff'), 0.4, 15);
    p3.position.set(0, 6, 0);
    scene.add(p3);

    // Helix root group — rotated each frame for the spin + breath.
    const root = new THREE.Group();
    root.add(buildStrand(0, '#7AB7FF', '#1E4E8C'));
    root.add(buildStrand(Math.PI, '#CFE0FF', '#1E4E8C'));
    root.add(buildRungs(0, Math.PI));
    scene.add(root);

    const clock = new THREE.Clock();
    const animate = () => {
      const t = clock.getElapsedTime();
      root.rotation.y = t * 0.18;
      root.position.y = Math.sin(t * 0.4) * 0.15;
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(animate);
    };
    animate();

    // Responsive resize
    const handleResize = () => {
      const w = container.clientWidth || window.innerWidth;
      const h = container.clientHeight || window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', handleResize);
      // Dispose all geometries + materials to prevent WebGL leaks.
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          (Array.isArray(obj.material) ? obj.material : [obj.material]).forEach((m) => m.dispose());
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 ${className}`}
      aria-hidden="true"
    />
  );
}
